import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { Loader2, Plus, RefreshCw, Search, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { authClient } from "../lib/auth-client";
import { usersConfigApi } from "./users-config/users-config-api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import type {
  ManagedUser,
  CreateUserFormData,
  AccountRole,
  ConvertibleAccountType,
  EditUserFormData,
  UserAccount,
} from "./users-config";
import {
  PAGE_SIZE,
  ACCOUNT_ROLE_LABELS,
  isUserBanned,
} from "./users-config";
import { BulkCreateUsersDialog } from "./users-config/BulkCreateUsersDialog";
import type { BulkUserInput } from "./users-config/bulk-user-schema";
import { CreateUserDialog } from "./users-config/CreateUserDialog";
import { DirectoryCard } from "./users-config/DirectoryCard";
import { EditUserDialog } from "./users-config/EditUserDialog";
import { ManageTrialDialog } from "./users-config/ManageTrialDialog";
import { ConfirmExtendDialog } from "./users-config/ConfirmExtendDialog";
import { ConfirmRevokeDialog } from "./users-config/ConfirmRevokeDialog";
import { ReactivateTrialDialog } from "./users-config/ReactivateTrialDialog";
import { SupportLoginDialog } from "./users-config/SupportLoginDialog";
import { SupportLoginActionsProvider } from "./users-config/SupportLoginActionsContext";
import { useTrialsForUsers } from "./users-config/useTrialsForUsers";
import { TrialActionsProvider } from "./users-config/TrialActionsContext";
import type { Trial, TrialCount } from "./users-config/trials-api";
import {
  apiErrorMessage,
  getTrialCount,
  updateUserAccount,
} from "./users-config/trials-api";
import { createUserAndAssignRoles } from "./users-config/role-operations";
import { updateCoteachers } from "./users-config/coteaching-api";

const SEARCH_DEBOUNCE_MS = 250;

type ListUsersQuery = {
  limit: number;
  offset: number;
};

type SearchUsersResponse = {
  users?: ManagedUser[];
  hasMore?: boolean;
  limit?: number | string;
  offset?: number | string;
};

export const UsersConfig = () => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [searchFilters, setSearchFilters] = useState<FilterValues>({});
  const [searchOptions, setSearchOptions] = useState<{ organizations: { id: string; name: string }[]; teachers: { id: number; name: string }[]; classrooms: { id: number; name: string }[] }>({ organizations: [], teachers: [], classrooms: [] });
  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      fetch("/api/internal/admin/organizations", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : []),
      fetch("/api/internal/admin/users/search-options", { credentials: "include", signal: controller.signal }).then((r) => r.ok ? r.json() : { teachers: [], classrooms: [] }),
    ]).then(([organizations, options]) => setSearchOptions({ organizations, ...options })).catch(() => {});
    return () => controller.abort();
  }, []);
  const hasFilters = Object.values(searchFilters).some(Boolean);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isBulkCreating, setIsBulkCreating] = useState(false);
  const [actionUserId, setActionUserId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [formData, setFormData] = useState<CreateUserFormData>({
    name: "",
    email: "",
    password: "",
    username: "",
    displayUsername: "",
    account_role: "ORG_ADMIN",
    organizationId: undefined,
    organizationName: "",
    grade: "",
  });

  // Edit user dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<ManagedUser | null>(null);
  // Bumped on every handleEdit call so a stale in-flight refresh can't overwrite a newer one.
  const editRequestIdRef = useRef(0);
  const [deletingUser, setDeletingUser] = useState<ManagedUser | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<EditUserFormData>({
    name: "",
    email: "",
    username: "",
    displayUsername: "",
  });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [manageTrialState, setManageTrialState] = useState<{
    user: ManagedUser;
    trial: Trial | null;
  } | null>(null);
  const [confirmExtendState, setConfirmExtendState] = useState<{
    user: ManagedUser;
    trial: Trial;
    days: number;
  } | null>(null);
  const [confirmRevokeUser, setConfirmRevokeUser] = useState<ManagedUser | null>(
    null,
  );
  const [confirmRoleChange, setConfirmRoleChange] = useState<{
    user: ManagedUser;
    role: AccountRole;
  } | null>(null);
  const [reactivateTrialUser, setReactivateTrialUser] =
    useState<ManagedUser | null>(null);
  const [supportLoginUser, setSupportLoginUser] = useState<ManagedUser | null>(
    null,
  );

  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { setActions } = useHeaderActions();

  const userIds = useMemo(() => users.map((u) => u.id), [users]);
  const { trials, refresh: refreshTrials } = useTrialsForUsers(userIds);
  const [trialCount, setTrialCount] = useState<TrialCount | null>(null);

  const refreshTrialCount = useCallback(async () => {
    try {
      const c = await getTrialCount();
      setTrialCount(c);
    } catch {
      setTrialCount(null);
    }
  }, []);

  useEffect(() => {
    refreshTrialCount();
  }, [refreshTrialCount]);

  const hasActiveSearch = debouncedSearchValue.trim().length > 0 || hasFilters;
  const totalPages = hasActiveSearch
    ? Math.max(1, page + (searchHasMore ? 1 : 0))
    : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingFrom = users.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = hasActiveSearch
    ? (users.length === 0 ? 0 : (page - 1) * PAGE_SIZE + users.length)
    : Math.min(page * PAGE_SIZE, total);
  const hasSearch = searchValue.trim().length > 0 || hasFilters;
  const resultCountLabel = hasActiveSearch
    ? `${users.length} match${users.length === 1 ? "" : "es"} shown`
    : `${total} users`;
  const paginationTotalLabel = hasActiveSearch
    ? `${showingFrom}-${showingTo}${
        searchHasMore ? " of more matches" : ` of ${showingTo} matches`
      }`
    : undefined;
  const paginationPageLabel = hasActiveSearch ? `Page ${page}` : undefined;

  const summary = useMemo(() => {
    const banned = users.filter(isUserBanned).length;
    return { banned };
  }, [users]);

  // Non-admin accounts only; admin accounts live in Dashboard IAM.
  const listUsersPage = useCallback(async (query: ListUsersQuery) => {
    const payload = await usersConfigApi.listUsers(query);
    const loadedUsers = payload.users ?? [];
    return {
      users: loadedUsers,
      total: payload.total ?? loadedUsers.length,
      hasMore: false,
    };
  }, []);

  const loadUserAccounts = useCallback(
    async (
      loadedUsers: ManagedUser[],
      signal?: AbortSignal,
    ): Promise<ManagedUser[]> => {
      if (loadedUsers.length === 0) return loadedUsers;

      try {
        const accountsUrl = "/api/internal/admin/users/accounts";
        const accountsRes = await fetch(accountsUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userIds: loadedUsers.map((u) => u.id) }),
          signal,
        });

        if (!accountsRes.ok) return loadedUsers;

        const accountsData = (await accountsRes.json()) as {
          accounts: UserAccount[];
        };
        const providersByUser = new Map<string, string[]>();
        for (const account of accountsData.accounts) {
          const userIdStr = String(account.userId);
          const existing = providersByUser.get(userIdStr) || [];
          if (!existing.includes(account.providerId)) {
            existing.push(account.providerId);
          }
          providersByUser.set(userIdStr, existing);
        }

        return loadedUsers.map((user) => ({
          ...user,
          providers: providersByUser.get(String(user.id)) || [],
        }));
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") throw error;
        // If accounts fetch fails, just show users without providers
        return loadedUsers;
      }
    },
    [],
  );

  const loadTeacherInfo = useCallback(
    async (
      loadedUsers: ManagedUser[],
      signal?: AbortSignal,
    ): Promise<ManagedUser[]> => {
      if (loadedUsers.length === 0) return loadedUsers;

      try {
        const res = await fetch("/api/internal/admin/users/teacher-info", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ userIds: loadedUsers.map((u) => u.id) }),
          signal,
        });

        if (!res.ok) return loadedUsers;

        const data = (await res.json()) as {
          teacherInfo: Record<
            string,
            {
              userId: string;
              teacherId: number | null;
              coTeacherCount: number;
              organizationId: string | null;
            }
          >;
        };

        return loadedUsers.map((user) => {
          const info = data.teacherInfo[String(user.id)];
          if (!info) return user;
          return {
            ...user,
            teacherId: info.teacherId,
            coTeacherCount: info.coTeacherCount,
            organizationId: info.organizationId ?? user.organizationId ?? null,
          };
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") throw error;
        return loadedUsers;
      }
    },
    [],
  );

  const loadSearchUsers = useCallback(
    async (query: string, signal?: AbortSignal) => {
      const params = new URLSearchParams({
        query,
        limit: PAGE_SIZE.toString(),
        offset: ((page - 1) * PAGE_SIZE).toString(),
      });
      Object.entries(searchFilters).forEach(([key, value]) => { if (value) params.set(key, value); });
      const response = await fetch(`/api/internal/admin/users/search?${params}`, {
        credentials: "include",
        signal,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(payload?.error ?? "Failed to search users");
      }

      const payload = (await response.json()) as SearchUsersResponse;
      const loadedUsers = payload.users ?? [];
      const hasMore = Boolean(payload.hasMore);

      return {
        users: loadedUsers,
        total: (page - 1) * PAGE_SIZE + loadedUsers.length + (hasMore ? 1 : 0),
        hasMore,
      };
    },
    [page, searchFilters],
  );

  const loadUsers = useCallback(async (signal?: AbortSignal) => {
    try {
      setIsLoading(true);
      const trimmedSearch = debouncedSearchValue.trim();

      const {
        users: loadedUsers,
        total: loadedTotal,
        hasMore,
      } = trimmedSearch || hasFilters
        ? await loadSearchUsers(trimmedSearch, signal)
        : await listUsersPage({
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
          });

      if (signal?.aborted) return;

      const usersWithAccounts = await loadUserAccounts(loadedUsers, signal);
      if (signal?.aborted) return;

      const usersWithTeacherInfo = await loadTeacherInfo(usersWithAccounts, signal);
      if (signal?.aborted) return;

      setTotal(loadedTotal);
      setSearchHasMore(Boolean(hasMore));
      setUsers(usersWithTeacherInfo);
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [
    debouncedSearchValue,
    hasFilters,
    listUsersPage,
    loadSearchUsers,
    loadUserAccounts,
    loadTeacherInfo,
    page,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    loadUsers(controller.signal);
    return () => controller.abort();
  }, [loadUsers]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearchValue(searchValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    const handleSlash = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      )
        return;
      if (event.key === "/") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleSlash);
    return () => window.removeEventListener("keydown", handleSlash);
  }, []);

  const checkUsernameAvailability = useCallback(
    async (
      username: string,
    ): Promise<{ available: true } | { available: false; message: string }> => {
      const trimmed = username.trim();
      if (!trimmed) return { available: true };

      try {
        const { data, error } = await authClient.isUsernameAvailable({
          username: trimmed,
        });

        if (error) {
          return {
            available: false,
            message: error.message ?? "Unable to verify username availability.",
          };
        }

        if (!data?.available) {
          return {
            available: false,
            message: "Username is already taken.",
          };
        }

        return { available: true };
      } catch {
        return {
          available: false,
          message: "Unable to verify username availability.",
        };
      }
    },
    [],
  );

  const createUserWithRoles = async (input: {
    email: string;
    password: string;
    name: string;
    username?: string;
    displayUsername?: string;
    account_role: AccountRole;
    organizationId?: string;
    grade?: string;
  }) => {
    const displayUsername = input.displayUsername;
    return createUserAndAssignRoles(
      {
        // Goes through our server (tab-guarded), which calls et-auth with the
        // service token. Always created as a non-admin `user`.
        createUser: async ({ email, password, name, username }) =>
          usersConfigApi.createUser({
            email,
            password,
            name,
            username,
            displayUsername: username ? displayUsername || username : undefined,
            signupRole: input.account_role === "TEACHER" ? "TEACHER" : "STUDENT",
            organizationId: input.organizationId,
            grade: input.account_role === "STUDENT" ? input.grade : undefined,
          }),
        updateAccount: async ({ userId, account_role }) =>
          updateUserAccount(userId, { account_role }),
      },
      input,
    );
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsCreating(true);

    try {
      const isOrgAdmin = formData.account_role === "ORG_ADMIN";
      if (formData.account_role === "STUDENT" && !formData.organizationId?.trim()) {
        toast.error("Select an organization for the student.");
        return;
      }
      const displayName =
        formData.name.trim() || formData.email.split("@")[0] || "User";
      // Org Admins don't take a username; everyone else may.
      const username = isOrgAdmin
        ? undefined
        : formData.username.trim() || undefined;
      if (username) {
        const availability = await checkUsernameAvailability(username);
        if (!availability.available) {
          toast.error(`${username}: ${availability.message}`);
          return;
        }
      }

      // For an Org Admin, create the organization they will manage first so we
      // can assign them to it after the account exists.
      let organizationId = formData.organizationId;
      if (isOrgAdmin) {
        const orgName = formData.organizationName?.trim();
        if (!orgName) {
          toast.error("Organization name is required");
          return;
        }
        const orgRes = await fetch("/api/internal/admin/organizations", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: orgName }),
        });
        if (!orgRes.ok) {
          toast.error("Failed to create organization");
          return;
        }
        const org = (await orgRes.json()) as { id?: string };
        organizationId = org.id;
      }

      const result = await createUserWithRoles({
        email: formData.email,
        password: formData.password,
        name: displayName,
        username,
        displayUsername: isOrgAdmin
          ? undefined
          : formData.displayUsername.trim() || undefined,
        account_role: formData.account_role,
        organizationId,
        grade: formData.grade,
      });

      if (!result.success) {
        toast.error(
          result.stage === "account"
            ? `User created, but account setup failed: ${result.message}`
            : result.message,
        );
        return;
      }

      // For a new Teacher with pre-selected co-teachers, resolve the freshly
      // created teacher row's id (via the teacher-info lookup) and persist the
      // co-teach target list.
      const coTeacherIds = formData.coTeacherIds ?? [];
      if (
        formData.account_role === "TEACHER" &&
        coTeacherIds.length > 0 &&
        result.userId
      ) {
        try {
          const infoRes = await fetch(
            "/api/internal/admin/users/teacher-info",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({ userIds: [String(result.userId)] }),
            },
          );
          if (infoRes.ok) {
            const data = (await infoRes.json()) as {
              teacherInfo: Record<string, { teacherId: number }>;
            };
            const teacherId = data.teacherInfo[String(result.userId)]?.teacherId;
            if (teacherId) {
              await updateCoteachers(teacherId, coTeacherIds);
            }
          }
        } catch (e) {
          console.error("Failed to set co-teachers for new teacher:", e);
          toast.error("User created, but co-teachers could not be saved");
        }
      }

      setFormData({ name: "", email: "", password: "", username: "", displayUsername: "", account_role: "ORG_ADMIN", organizationId: undefined, organizationName: "", coTeacherIds: [], grade: "" });
      setIsCreateDialogOpen(false);
      setPage(1);
      await loadUsers();
      toast.success("User created successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to create user");
    } finally {
      setIsCreating(false);
    }
  };

  const handleBulkCreate = async (entries: BulkUserInput[]): Promise<boolean> => {
    setIsBulkCreating(true);
    try {
      const result = await usersConfigApi.bulkCreateUsers(entries.map((entry) => ({
        email: entry.email, password: entry.password, name: entry.name, username: entry.username,
        signupRole: entry.account_role === "TEACHER" ? "TEACHER" : "STUDENT",
        organization: entry.organization, grade: entry.grade, classroom: entry.classroom,
      })));
      setPage(1);
      await loadUsers();
      toast.success(`Created ${result.results.length} users successfully.`);
      return true;
    } finally { setIsBulkCreating(false); }
  };

  const applyAccountPatch = async (
    userId: string,
    patch: { account_role: AccountRole } | { account_type: ConvertibleAccountType },
  ) => {
    const label = "account_role" in patch ? "Role" : "Type";
    try {
      setActionUserId(userId);
      const prevUser = users.find((u) => u.id === userId);
      await updateUserAccount(userId, patch);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
      );
      toast.success(`${label} set to ${"account_role" in patch ? patch.account_role : patch.account_type}`);
      // Converting a demo user away from demo deletes their trial row on the
      // backend — refresh trial state so the directory pill/count reflect it.
      if ("account_type" in patch && prevUser?.account_type === "demo") {
        refreshTrials();
        refreshTrialCount();
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, `Failed to update ${label.toLowerCase()}`));
    } finally {
      setActionUserId(null);
    }
  };

  const handleAccountTypeChange = (userId: string, type: ConvertibleAccountType) => {
    const user = users.find((u) => u.id === userId);
    if (!user || user.account_type === type) return;
    void applyAccountPatch(userId, { account_type: type });
  };

  // Start trial and Reactivate also set account_type to demo on the backend.
  // Mirror that here, so the row badge and the Type menu match without a reload.
  const markAccountDemo = (userId: string) =>
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, account_type: "demo" } : u)),
    );

  const handleAccountRoleChange = (userId: string, role: AccountRole) => {
    const user = users.find((u) => u.id === userId);
    if (!user || user.account_role === role) return;
    setConfirmRoleChange({ user, role });
  };

  const handleConfirmRoleChange = async () => {
    if (!confirmRoleChange) return;
    const { user, role } = confirmRoleChange;
    await applyAccountPatch(user.id, { account_role: role });
    setConfirmRoleChange(null);
  };

  const isRoleChangeSubmitting =
    confirmRoleChange != null && actionUserId === confirmRoleChange.user.id;

  const handleBanToggle = async (user: ManagedUser) => {
    try {
      setActionUserId(user.id);
      const isBanned = isUserBanned(user);
      if (isBanned) await usersConfigApi.unbanUser(user.id);
      else await usersConfigApi.banUser(user.id);

      setUsers((prev) =>
        prev.map((item) =>
          item.id === user.id
            ? {
                ...item,
                banned: !isBanned,
                banReason: isBanned ? null : "No reason",
                banExpiresAt: isBanned ? null : undefined,
              }
            : item,
        ),
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to update user status");
    } finally {
      setActionUserId(null);
    }
  };

  const handleResetPassword = async (user: ManagedUser) => {
    const newPassword = prompt(`Enter new password for ${user.email}:`);
    if (!newPassword) return;

    try {
      setActionUserId(user.id);
      await usersConfigApi.setPassword(user.id, newPassword);

      toast.success(`Password reset for ${user.email}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset password");
    } finally {
      setActionUserId(null);
    }
  };

  const handleDeleteUser = (user: ManagedUser) => {
    setDeletingUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteDialogOpenChange = (open: boolean) => {
    if (deletingUser && actionUserId === deletingUser.id) return;
    setIsDeleteDialogOpen(open);
    if (!open) {
      setDeletingUser(null);
    }
  };

  const handleConfirmDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      setActionUserId(deletingUser.id);
      await usersConfigApi.deleteUser(deletingUser.id);

      toast.success(`User ${deletingUser.email} deleted`);
      setIsDeleteDialogOpen(false);
      setDeletingUser(null);
      await loadUsers();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete user");
    } finally {
      setActionUserId(null);
    }
  };

  const handleRevokeAllSessions = async (user: ManagedUser) => {
    try {
      setActionUserId(user.id);
      await usersConfigApi.revokeSessions(user.id);

      toast.success(`All sessions revoked for ${user.email}`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to revoke sessions");
    } finally {
      setActionUserId(null);
    }
  };

  const applyEditFormFrom = (user: ManagedUser) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name ?? "",
      email: user.email ?? "",
      username: user.username ?? "",
      displayUsername: user.displayUsername ?? "",
      organizationId: user.organizationId ?? undefined,
      grade: user.grade ?? "",
    });
  };

  const handleEdit = (user: ManagedUser) => {
    // Open with the cached row now, then quietly refresh in case it's stale.
    applyEditFormFrom(user);
    setIsEditDialogOpen(true);

    const requestId = ++editRequestIdRef.current;
    fetch("/api/internal/admin/users/by-ids", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ userIds: [user.id] }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { users: ManagedUser[] } | null) => {
        const fresh = data?.users?.[0];
        if (fresh && editRequestIdRef.current === requestId) {
          applyEditFormFrom(fresh);
        }
      })
      .catch((e) => console.error("Failed to refresh user before editing:", e));
  };

  const handleSaveEdit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingUser) return;

    setIsSavingEdit(true);
    try {
      await usersConfigApi.updateProfile(editingUser.id, {
        name: editFormData.name,
        email: editFormData.email,
        username: editFormData.username,
        displayUsername: editFormData.displayUsername,
      });

      const orgPatchRes = await fetch(
        `/api/internal/admin/users/${editingUser.id}/organization`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ organizationId: editFormData.organizationId ?? null }),
        },
      );
      if (!orgPatchRes.ok) {
        const body = await orgPatchRes.text();
        console.error("Failed to set organization on edit:", orgPatchRes.status, body);
        toast.error("User updated, but organization could not be set");
      }

      if (editingUser.account_role === "STUDENT") {
        const gradePatchRes = await fetch(
          `/api/internal/admin/users/${editingUser.id}/grade`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ grade: editFormData.grade || null }),
          },
        );
        if (!gradePatchRes.ok) {
          const body = await gradePatchRes.text();
          console.error("Failed to set grade on edit:", gradePatchRes.status, body);
          toast.error("User updated, but grade could not be set");
        }
      }

      // Update local state
      setUsers((prev) =>
        prev.map((user) =>
          user.id === editingUser.id
            ? {
                ...user,
                name: editFormData.name,
                email: editFormData.email,
                username: editFormData.username,
                displayUsername: editFormData.displayUsername,
                organizationId: editFormData.organizationId ?? null,
                ...(editingUser.account_role === "STUDENT"
                  ? { grade: editFormData.grade || null }
                  : {}),
              }
            : user,
        ),
      );

      setIsEditDialogOpen(false);
      setEditingUser(null);
      toast.success("User updated successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update user");
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Set header actions
  useEffect(() => {
    setActions(
      <>
        <Button
          variant="outline"
          size="icon"
          onClick={() => loadUsers()}
          disabled={isLoading}
          className="h-8 w-8"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
        <Button
          variant="outline"
          onClick={() => setIsBulkDialogOpen(true)}
          className="h-8 gap-2"
        >
          <Upload className="h-4 w-4" />
          Bulk add
        </Button>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          className="h-8 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add user
        </Button>
      </>,
    );
    return () => setActions(null);
  }, [setActions, isLoading, loadUsers]);

  return (
    <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
      <div className="flex h-full flex-col gap-4 overflow-hidden">
        {/* Search bar */}
        <Card className="rounded-xl border-border/60 p-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search by name, email, or username..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="pl-9 pr-14"
              />
              <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                /
              </kbd>
            </div>
            <AdvancedOptions values={searchFilters} onChange={(values) => { setPage(1); setSearchFilters(values); }} fields={[
              { key: "organization_id", label: "Organization", options: searchOptions.organizations.map((org) => ({ value: org.id, label: org.name })) },
              { key: "account_role", label: "Account role", options: ["STUDENT", "TEACHER", "ORG_ADMIN"].map((role) => ({ value: role, label: ACCOUNT_ROLE_LABELS[role as AccountRole] })) },
              { key: "teacher_id", label: "Teacher", options: searchOptions.teachers.map((teacher) => ({ value: String(teacher.id), label: `${teacher.name} (#${teacher.id})` })) },
              { key: "classroom_id", label: "Classroom", options: searchOptions.classrooms.map((classroom) => ({ value: String(classroom.id), label: `${classroom.name} (#${classroom.id})` })) },
            ]} />
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span>
                {resultCountLabel} • {summary.banned} banned
              </span>
              {trialCount !== null && (
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    trialCount.ending_soon > 0
                      ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200"
                      : "bg-blue-50 text-blue-900 ring-1 ring-blue-200"
                  }`}
                  title={`active: ${trialCount.active} • ending soon: ${trialCount.ending_soon} • expired: ${trialCount.expired} • revoked: ${trialCount.revoked}`}
                >
                  {trialCount.active + trialCount.ending_soon} on trial
                  {trialCount.ending_soon > 0 && (
                    <span className="font-semibold">
                      ({trialCount.ending_soon} ending soon)
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </Card>

        <SupportLoginActionsProvider
          value={{ onSupportLogin: (user) => setSupportLoginUser(user) }}
        >
        <TrialActionsProvider
          value={{
            onStartTrial: (user) => setManageTrialState({ user, trial: null }),
            onManageTrial: (user, trial) => setManageTrialState({ user, trial }),
            onReactivateTrial: (user) => setReactivateTrialUser(user),
          }}
        >
          <DirectoryCard
            users={users}
            total={total}
            summary={summary}
            showingFrom={showingFrom}
            showingTo={showingTo}
            page={page}
            totalPages={totalPages}
            totalLabel={paginationTotalLabel}
            pageLabel={paginationPageLabel}
            hasNextPage={hasActiveSearch ? searchHasMore : undefined}
            isLoading={isLoading}
            actionUserId={actionUserId}
            hasSearch={hasSearch}
            trials={trials}
            onPageChange={setPage}
            onClearSearch={() => { setSearchValue(""); setSearchFilters({}); setPage(1); }}
            onEdit={handleEdit}
            onResetPassword={handleResetPassword}
            onRevokeSessions={handleRevokeAllSessions}
            onAccountRoleChange={handleAccountRoleChange}
            onAccountTypeChange={handleAccountTypeChange}
            onBanToggle={handleBanToggle}
            onDelete={handleDeleteUser}
          />
        </TrialActionsProvider>
        </SupportLoginActionsProvider>
      </div>

      <CreateUserDialog
        isOpen={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleCreate}
        isCreating={isCreating}
      />

      <EditUserDialog
        isOpen={isEditDialogOpen}
        onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) setEditingUser(null);
        }}
        user={editingUser}
        formData={editFormData}
        onFormDataChange={setEditFormData}
        onSubmit={handleSaveEdit}
        isSaving={isSavingEdit}
        onCoTeacherCountChange={(userId, count) =>
          setUsers((prev) =>
            prev.map((u) =>
              u.id === userId ? { ...u, coTeacherCount: count } : u,
            ),
          )
        }
      />

      <BulkCreateUsersDialog
        isOpen={isBulkDialogOpen}
        onOpenChange={setIsBulkDialogOpen}
        onCreateUsers={handleBulkCreate}
        isCreating={isBulkCreating}
      />

      <ManageTrialDialog
        user={manageTrialState?.user ?? null}
        trial={manageTrialState?.trial ?? null}
        open={Boolean(manageTrialState)}
        onClose={() => setManageTrialState(null)}
        onExtend={(days) => {
          if (!manageTrialState?.trial) return;
          setConfirmExtendState({
            user: manageTrialState.user,
            trial: manageTrialState.trial,
            days,
          });
        }}
        onRevoke={() => {
          if (!manageTrialState) return;
          setConfirmRevokeUser(manageTrialState.user);
        }}
        onStarted={() => {
          if (manageTrialState) markAccountDemo(manageTrialState.user.id);
          refreshTrials();
          refreshTrialCount();
        }}
      />

      <ConfirmExtendDialog
        user={confirmExtendState?.user ?? null}
        trial={confirmExtendState?.trial ?? null}
        additionalDays={confirmExtendState?.days ?? 0}
        open={Boolean(confirmExtendState)}
        onClose={() => setConfirmExtendState(null)}
        onSuccess={() => {
          setManageTrialState(null);
          refreshTrials();
          refreshTrialCount();
        }}
      />

      <ConfirmRevokeDialog
        user={confirmRevokeUser}
        open={Boolean(confirmRevokeUser)}
        onClose={() => setConfirmRevokeUser(null)}
        onSuccess={() => {
          setManageTrialState(null);
          refreshTrials();
          refreshTrialCount();
        }}
      />

      {supportLoginUser ? (
        <SupportLoginDialog
          key={supportLoginUser.id}
          user={supportLoginUser}
          open
          onClose={() => setSupportLoginUser(null)}
        />
      ) : null}

      <ReactivateTrialDialog
        user={reactivateTrialUser}
        open={Boolean(reactivateTrialUser)}
        onClose={() => setReactivateTrialUser(null)}
        onSuccess={() => {
          if (reactivateTrialUser) markAccountDemo(reactivateTrialUser.id);
          refreshTrials();
          refreshTrialCount();
        }}
      />

      <Dialog
        open={Boolean(confirmRoleChange)}
        onOpenChange={(open) => {
          if (isRoleChangeSubmitting) return;
          if (!open) setConfirmRoleChange(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription>
              {confirmRoleChange
                ? `Change ${confirmRoleChange.user.email}'s role from ${
                    confirmRoleChange.user.account_role
                      ? ACCOUNT_ROLE_LABELS[confirmRoleChange.user.account_role]
                      : "none"
                  } to ${ACCOUNT_ROLE_LABELS[confirmRoleChange.role]}?`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRoleChange(null)}
              disabled={isRoleChangeSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmRoleChange}
              disabled={!confirmRoleChange || isRoleChangeSubmitting}
            >
              {isRoleChangeSubmitting && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Change Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onOpenChange={handleDeleteDialogOpenChange}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              {deletingUser
                ? `Are you sure you want to delete ${deletingUser.email}? This action cannot be undone.`
                : "Are you sure you want to delete this user? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleDeleteDialogOpenChange(false)}
              disabled={!!deletingUser && actionUserId === deletingUser.id}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDeleteUser}
              disabled={!deletingUser || actionUserId === deletingUser?.id}
            >
              {deletingUser && actionUserId === deletingUser.id && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};
